/**
 * Per-flight cost ledger.
 *
 * One `CostLineItem` list is the company flight-cost picture (rollup + ops
 * snapshot). There is no second row type.
 *
 * Fuel, landing, handling, and catering post from `company.opsDesk.costRates`
 * when a trip is on the board (accepted — not cancelled). Those are trip fees.
 * They are not part of variable hourly DOC.
 *
 * On complete, optional variable insurance (type, else tail override) posts
 * as hours × insurancePerHour. When that component is unset, the company
 * `mxInsurancePerHour` placeholder still posts. Hours are the post-flight
 * clock: AAR engine time, else SimConnect combustion/N1, else SimBrief OFP,
 * else planned ETE. A rate of 0 means unset and is
 * skipped — no $0 line. The same sector × category posts once. Removing an
 * auto line voids that id so a later sync does not put it back.
 *
 * When `rates` is passed, a completed leg also posts `mx_reserve` from the
 * same quote the company ledger uses, and replaces that line when later
 * hours arrive. Omitting `rates` leaves existing callers unchanged.
 *
 * Fuel quantity is the sector burn converted into the tail's fuel unit
 * (company default when the tail has no override) times `fuelPerUnit`.
 * Variable DOC fuel planning does not post a second fuel line.
 * Catering is the rate times passengers on that sector. Landing and handling
 * are flat per sector.
 *
 * Amounts are company costs: negative. A typed amount is stored negative.
 *
 * A pax or cargo edit recalculates fuel, catering, and weight in one Rust
 * pass. That pass updates the existing catering line. It does not delete it.
 * An already-posted line is not replaced when a later arrival burn or block
 * time shows up, except by that pax recalc.
 */
import { resolveAircraftOperatingCost } from "./aircraftOperatingCost";
import { fuelKindForType } from "./catalogs";
import { DATA_SOURCE_LABEL } from "./dataSource";
import { convertFuel, formatFuelAmount, hydrateFuelUnit, resolveAircraftFuelUnit } from "./fuelUnits";
import { ofpPlannedBlockHours, quoteCharter, resolveBillableHours } from "./ledger";
import { hydrateOpsDeskSettings } from "./opsDeskSettings";
import { findTripTelemetry, tripEngineHoursForReserve, tripHoursForQuote, type TailTripTelemetry } from "./simTelemetry";
import {
  hydrateCostLine,
  hydrateCostLines,
  type CostLineCategory,
  type CostLineItem,
  type OpsMoneyCode,
} from "./opsLedger";
import { opsMoneyCode } from "./crewPersonalLedger";
import type { AircraftSpec, CompanyProfile, FlightLogEntry, Mission, OpsCostRates, RateTables } from "./types";

export const COST_LEDGER_CAP = 400;

const SECTOR_CATEGORIES = ["fuel", "landing", "handling", "catering"] as const;
type SectorCategory = (typeof SECTOR_CATEGORIES)[number];

const SECTOR_RATE_KEY: Record<SectorCategory, keyof OpsCostRates> = {
  fuel: "fuelPerUnit",
  landing: "landing",
  handling: "handling",
  catering: "catering",
};

export type CostLedgerLineInput = {
  category?: CostLineCategory;
  /** Positive magnitude or an already-negative cost. Zero is unset and is not posted. */
  amount: number;
  currency?: OpsMoneyCode;
  missionId?: string;
  flightNumber?: string;
  aircraftId?: string;
  memo?: string;
  id?: string;
  createdAtUtc?: string;
};

export type CostLedgerRollup = {
  count: number;
  total: number;
  byCategory: Record<CostLineCategory, number>;
};

type Sector = {
  seq: number;
  fuelGal: number;
  fuelLbs: number;
  paxCount: number;
  origin: string;
  dest: string;
};

function text(raw: unknown, max: number): string {
  return String(raw ?? "").trim().slice(0, max);
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** Stable id for one auto cost on one sector (or the trip, for mx). Fits the 64-char hydrate cap. */
export function flightCostLineId(missionId: string, category: CostLineCategory, seq?: number): string {
  const sector = seq && seq > 0 ? `:${seq}` : "";
  const raw = `cst:${missionId}${sector}:${category}`;
  if (raw.length <= 64) return raw;
  return `cst:${fnv1a(raw)}:${category}`.slice(0, 64);
}

export function isFlightCostAutoId(id: string): boolean {
  return id.startsWith("cst:");
}

/** Company-cost sign. Zero / non-finite is unset and is not a line. */
export function flightCostAmount(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  const signed = n > 0 ? -n : n;
  return Math.round(signed * 100) / 100;
}

function capped(rows: CostLineItem[]): CostLineItem[] {
  return rows.slice(0, COST_LEDGER_CAP);
}

export function hydrateCostLedgerVoids(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const id = text(row, 64);
    if (!isFlightCostAutoId(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= COST_LEDGER_CAP) break;
  }
  return out;
}

export function linesForMission(ledger: readonly CostLineItem[] | null | undefined, missionId: string): CostLineItem[] {
  const id = text(missionId, 64);
  if (!id) return [];
  return hydrateCostLines(ledger).filter((row) => row.missionId === id);
}

export function summarizeCostLedger(ledger: readonly CostLineItem[] | null | undefined): CostLedgerRollup {
  const rows = hydrateCostLines(ledger);
  const byCategory: Record<CostLineCategory, number> = {
    fuel: 0,
    landing: 0,
    handling: 0,
    catering: 0,
    mx_insurance: 0,
    mx_reserve: 0,
    other: 0,
  };
  let total = 0;
  for (const row of rows) {
    byCategory[row.category] = Math.round((byCategory[row.category] + row.amount) * 100) / 100;
    total = Math.round((total + row.amount) * 100) / 100;
  }
  return { count: rows.length, total, byCategory };
}

export function appendCostLedgerLine(
  ledger: readonly CostLineItem[] | null | undefined,
  input: CostLedgerLineInput,
  now = new Date(),
): { ledger: CostLineItem[]; entry: CostLineItem | null; error?: string } {
  const current = hydrateCostLines(ledger);
  const amount = flightCostAmount(input.amount);
  if (amount == null) return { ledger: current, entry: null, error: "Amount must be non-zero. Zero means unset." };
  const createdAtUtc = text(input.createdAtUtc, 40) || now.toISOString();
  const id = text(input.id, 64) || `cost-${now.getTime().toString(36)}`.slice(0, 64);
  if (current.some((row) => row.id === id)) return { ledger: current, entry: current.find((row) => row.id === id) ?? null };
  const entry = hydrateCostLine({
    id,
    category: input.category ?? "other",
    amount,
    currency: opsMoneyCode(input.currency),
    createdAtUtc,
    missionId: text(input.missionId, 64),
    flightNumber: text(input.flightNumber, 16),
    aircraftId: text(input.aircraftId, 64),
    memo: text(input.memo, 120),
  });
  if (!entry) return { ledger: current, entry: null, error: "Line could not be saved." };
  return { ledger: capped([entry, ...current]), entry };
}

export function patchCostLedgerLine(
  ledger: readonly CostLineItem[] | null | undefined,
  id: string,
  patch: { amount?: number; memo?: string },
): CostLineItem[] {
  const current = hydrateCostLines(ledger);
  const key = text(id, 64);
  return current.map((row) => {
    if (row.id !== key) return row;
    const amount = patch.amount === undefined ? row.amount : flightCostAmount(patch.amount);
    if (patch.amount !== undefined && amount == null) return row;
    const next = hydrateCostLine({
      ...row,
      amount: amount ?? row.amount,
      memo: patch.memo === undefined ? row.memo : text(patch.memo, 120),
    });
    return next ?? row;
  });
}

export function removeCostLedgerLine(
  ledger: readonly CostLineItem[] | null | undefined,
  voids: readonly string[] | null | undefined,
  id: string,
): { ledger: CostLineItem[]; voids: string[] } {
  const key = text(id, 64);
  const nextLedger = hydrateCostLines(ledger).filter((row) => row.id !== key);
  const nextVoids = hydrateCostLedgerVoids(voids);
  if (isFlightCostAutoId(key) && !nextVoids.includes(key)) nextVoids.push(key);
  return { ledger: nextLedger, voids: nextVoids.slice(0, COST_LEDGER_CAP) };
}

function sectorFuel(sector: Sector, unit: ReturnType<typeof resolveAircraftFuelUnit>, kind: ReturnType<typeof fuelKindForType>): number {
  if (sector.fuelGal > 0) return convertFuel(sector.fuelGal, "gal", unit, kind);
  if (sector.fuelLbs > 0) return convertFuel(sector.fuelLbs, "lb", unit, kind);
  return 0;
}

function sectorsFor(mission: Mission): Sector[] {
  const legs = mission.legs ?? [];
  if (!legs.length) {
    return [
      {
        seq: 1,
        fuelGal: mission.fuelGal,
        fuelLbs: mission.fuelLbs,
        paxCount: mission.paxCount,
        origin: mission.originIcao,
        dest: mission.destIcao,
      },
    ];
  }
  const seen = new Set<number>();
  const rows: Sector[] = legs.map((leg, index) => {
    let seq = Number.isFinite(leg.seq) && leg.seq > 0 ? Math.floor(leg.seq) : index + 1;
    while (seen.has(seq)) seq += 1;
    seen.add(seq);
    return {
      seq,
      fuelGal: leg.fuelGal,
      fuelLbs: leg.fuelLbs,
      paxCount: leg.paxCount,
      origin: leg.originIcao,
      dest: leg.destIcao,
    };
  });
  const legFuel = rows.some((row) => row.fuelGal > 0 || row.fuelLbs > 0);
  if (!legFuel && ((mission.fuelGal ?? 0) > 0 || (mission.fuelLbs ?? 0) > 0) && rows[0]) {
    rows[0] = { ...rows[0], fuelGal: mission.fuelGal, fuelLbs: mission.fuelLbs };
  }
  return rows;
}

function routeMemo(label: string, sector: Sector, flightNumber: string): string {
  const flight = flightNumber ? ` · ${flightNumber}` : "";
  return `${label} ${sector.origin}–${sector.dest}${flight}`.slice(0, 120);
}

function logFor(logs: readonly FlightLogEntry[] | null | undefined, mission: Mission): FlightLogEntry | undefined {
  return logs?.find((row) => row.tripId === mission.id || row.id === mission.id);
}

function tailFor(fleet: readonly AircraftSpec[], mission: Mission): AircraftSpec | undefined {
  return fleet.find((row) => row.id === mission.aircraftId) ?? fleet.find((row) => row.registration === mission.registration);
}

function pushLine(
  added: CostLineItem[],
  existing: Set<string>,
  voids: Set<string>,
  draft: CostLineItem,
): void {
  if (existing.has(draft.id) || voids.has(draft.id)) return;
  const entry = hydrateCostLine(draft);
  if (!entry) return;
  existing.add(entry.id);
  added.push(entry);
}

export type FlightCostPostInput = {
  ledger?: readonly CostLineItem[] | null;
  voids?: readonly string[] | null;
  missions?: readonly Mission[] | null;
  fleet?: readonly AircraftSpec[] | null;
  company?: Partial<CompanyProfile> | null;
  logs?: readonly FlightLogEntry[] | null;
  /** Sim trip hours, same source settle uses for Mx / engine reserves. */
  tripTelemetry?: readonly TailTripTelemetry[] | null;
  currency?: unknown;
  now?: Date;
  /** Mission ids where de-ice was flagged by evaluateDeice. */
  deiceMissionIds?: ReadonlySet<string> | null;
  /**
   * Company reserve rates. When set, a completed leg accrues `mx_reserve`
   * and replaces that line if the hours change. Omit to skip the accrual.
   */
  rates?: RateTables | null;
};

/**
 * Append missing flight cost lines from ops-desk rates.
 * Returns the same logical lines when that sector was already posted.
 */
export function postFlightCostLines(input: FlightCostPostInput): { ledger: CostLineItem[]; posted: number } {
  const current = hydrateCostLines(input.ledger);
  const voids = new Set(hydrateCostLedgerVoids(input.voids));
  const rates: OpsCostRates = hydrateOpsDeskSettings(input.company?.opsDesk).costRates;
  const currency = opsMoneyCode(input.currency);
  const now = input.now ?? new Date();
  const createdAtUtc = now.toISOString();
  const companyUnit = hydrateFuelUnit(input.company?.defaultFuelUnit);
  const fleet = input.fleet ?? [];
  const existing = new Set(current.map((row) => row.id));
  const added: CostLineItem[] = [];
  let rows = current;
  let replaced = 0;

  const upsertAuto = (draft: CostLineItem) => {
    if (voids.has(draft.id)) return;
    const entry = hydrateCostLine(draft);
    if (!entry) return;
    const idx = rows.findIndex((row) => row.id === entry.id);
    if (idx === -1) {
      pushLine(added, existing, voids, entry);
      return;
    }
    const prev = rows[idx]!;
    if (prev.amount === entry.amount && (prev.memo ?? "") === (entry.memo ?? "")) return;
    if (rows === current) rows = current.slice();
    rows[idx] = { ...prev, amount: entry.amount, memo: entry.memo, currency: entry.currency };
    replaced += 1;
  };

  for (const mission of input.missions ?? []) {
    if (!mission?.id || mission.status === "cancelled" || mission.crashed) continue;
    const flightNumber = text(mission.flightNumber || mission.callsign, 16);
    const aircraftId = text(mission.aircraftId, 64);
    const tail = tailFor(fleet, mission);
    const unit = resolveAircraftFuelUnit(tail, companyUnit);
    const kind = fuelKindForType(tail?.icaoType);

    for (const sector of sectorsFor(mission)) {
      for (const category of SECTOR_CATEGORIES) {
        const rate = rates[SECTOR_RATE_KEY[category]];
        if (!(rate > 0)) continue;
        let amount: number | null = null;
        let memo = "";
        if (category === "fuel") {
          const qty = sectorFuel(sector, unit, kind);
          if (!(qty > 0)) continue;
          amount = flightCostAmount(qty * rate);
          memo = routeMemo(`Fuel ${formatFuelAmount(qty, unit)}`, sector, flightNumber);
        } else if (category === "catering") {
          const pax = Math.max(0, Math.round(sector.paxCount || 0));
          if (!(pax > 0)) continue;
          amount = flightCostAmount(pax * rate);
          memo = routeMemo(`Catering ${pax} pax`, sector, flightNumber);
        } else if (category === "landing") {
          amount = flightCostAmount(rate);
          memo = routeMemo("Landing", sector, flightNumber);
        } else if (category === "handling") {
          amount = flightCostAmount(rate);
          memo = routeMemo("Handling", sector, flightNumber);
        } else {
          continue;
        }
        if (amount == null) continue;
        pushLine(added, existing, voids, {
          id: flightCostLineId(mission.id, category, sector.seq),
          category,
          amount,
          currency,
          createdAtUtc,
          missionId: mission.id,
          ...(flightNumber ? { flightNumber } : {}),
          ...(aircraftId ? { aircraftId } : {}),
          memo,
        });
      }
    }

    if (mission.status === "completed") {
      const doc = tail ? resolveAircraftOperatingCost(tail, input.company?.opsDesk) : null;
      const fromDoc = (doc?.insurancePerHour ?? 0) > 0;
      const hourly = fromDoc ? doc!.insurancePerHour : rates.mxInsurancePerHour;
      if (hourly > 0) {
        const log = logFor(input.logs, mission);
        const tel = findTripTelemetry(input.tripTelemetry ?? [], {
          tripId: mission.id,
          flightLogId: log?.id,
          registration: mission.registration,
        });
        const hoursPick = resolveBillableHours(mission, log, {
          simHours: tripHoursForQuote(tel) ?? undefined,
          ofpPlannedHours: ofpPlannedBlockHours(mission),
        });
        const hours = hoursPick?.value ?? 0;
        const amount = hours > 0 ? flightCostAmount(hours * hourly) : null;
        if (amount != null) {
          const source = DATA_SOURCE_LABEL[hoursPick?.source ?? "desk_estimate"];
          const flight = flightNumber ? ` · ${flightNumber}` : "";
          const label = fromDoc ? "Variable insurance" : "Mx / insurance";
          pushLine(added, existing, voids, {
            id: flightCostLineId(mission.id, "mx_insurance"),
            category: "mx_insurance",
            amount,
            currency,
            createdAtUtc,
            missionId: mission.id,
            ...(flightNumber ? { flightNumber } : {}),
            ...(aircraftId ? { aircraftId } : {}),
            memo: `${label} ${hours.toFixed(2)} h (${source})${flight}`.slice(0, 120),
          });
        }
      }
      if (input.rates) {
        const reserveLog = logFor(input.logs, mission);
        const reserveTel = findTripTelemetry(input.tripTelemetry ?? [], {
          tripId: mission.id,
          flightLogId: reserveLog?.id,
        });
        const reserveQuote = quoteCharter(mission, input.rates, reserveLog, {
          aircraft: tail,
          opsDesk: input.company?.opsDesk,
          simHours: tripEngineHoursForReserve(reserveTel) ?? undefined,
          ofpPlannedHours: ofpPlannedBlockHours(mission),
        });
        const reserveAmount = reserveQuote.mxReserveCad > 0 ? flightCostAmount(reserveQuote.mxReserveCad) : null;
        if (reserveAmount != null) {
          const flight = flightNumber ? ` · ${flightNumber}` : "";
          const source = DATA_SOURCE_LABEL[reserveQuote.hoursSource];
          upsertAuto({
            id: flightCostLineId(mission.id, "mx_reserve"),
            category: "mx_reserve",
            amount: reserveAmount,
            currency,
            createdAtUtc,
            missionId: mission.id,
            ...(flightNumber ? { flightNumber } : {}),
            ...(aircraftId ? { aircraftId } : {}),
            memo: `Mx reserve ${reserveQuote.hours.toFixed(2)} h (${source})${flight}`.slice(0, 120),
          });
        }
      }
    }

    if (rates.deice > 0 && input.deiceMissionIds?.has(mission.id)) {
      const deiceAmount = flightCostAmount(rates.deice);
      if (deiceAmount != null) {
        const flight = flightNumber ? ` · ${flightNumber}` : "";
        pushLine(added, existing, voids, {
          id: flightCostLineId(mission.id, "other", 0),
          category: "other",
          amount: deiceAmount,
          currency,
          createdAtUtc,
          missionId: mission.id,
          ...(flightNumber ? { flightNumber } : {}),
          ...(aircraftId ? { aircraftId } : {}),
          memo: `De-ice ${mission.originIcao}${flight}`.slice(0, 120),
        });
      }
    }
  }

  if (!added.length && replaced === 0) return { ledger: current, posted: 0 };
  return { ledger: capped([...added, ...rows]), posted: added.length + replaced };
}
